import { useState } from "react";
import { InputFile, Label } from "../../components";
import ErrorMessage from "../../components/Common/ErrorMessage";
import KYCStatus from "../../components/KYC/KYCStatus";
import { PrimaryButton } from "../../components/Buttons";
import OptionButton from "../../components/Common/OptionButton";
import SignaturePad from "../../components/DraftClient/Signature/partials/signaturePad";
import { UpdateRequestedKYC } from "../../redux/actions/auth";
import { useDispatch, useSelector } from "react-redux";
import { getCookie, removeCookie } from "../../utils/cookies";
import { GetValidationErrors } from "../../helper";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import { uploadSignatureForOldClients } from "../../redux/actions/ClientKYCGuest";
import { useSearchParams } from "react-router-dom";

const ClientSignatureUpload = () => {
  const [option, setOption] = useState("upload");
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ signature_photo: null });
  const [Success, setSuccess] = useState(false);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(false);
  
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");
  const client_id = searchParams.get("client_id");

  // 🔹 Validate before continuing
  const handleContinue = async () => {
    if (!formData.signature_photo) {
      setError("Please upload or draw your signature.");
      return;
    }
    setIsLoading(true);
    
    // Handle signature upload
    const form = new FormData();
    form.append("signature_photo", formData.signature_photo);
    form.append("hash", hash);
    form.append("client_id", client_id);

    try {
      const kyc_user = getCookie("kyc_user");
      const token = !user?.token && kyc_user?.token;

      const res = token ? await dispatch(uploadSignatureForOldClients(form, true)) : await dispatch(uploadSignatureForOldClients(form, false));

      if (res.errors) {
        setError(GetValidationErrors(res.errors));
      } else {
        setError(res.message || "Something went wrong please try again later.");
      }

      if (res.success) {
        if (user?.token) {
          await dispatch(UpdateRequestedKYC({ ...user, kyc_requested: 0 }));
        } else if (kyc_user?.token) {
          removeCookie("kyc_user");
        }

        // for marchant api users
        if (res.deep_link_redirection) {
          window.location.href = res.deep_link_url;
        }

        setSuccess(true);
      }
    } catch (error) {
      setError("Something went wrong please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 Clear signature
  const handleClearSignature = () => {
    setFormData({ signature_photo: null });
    setError("");
  };

  // 🔹 Handle file upload
  const handleFileChange = (e) => {
    setFormData({ signature_photo: e.target.files[0] });
    setError("");
  };
  
  const handleChangeOption = (option) => {
    setOption(option);
    setFormData({ signature_photo: null });
    setError("");
  };
  

  return (
    <div className="md:max-w-[992px] mx-auto min-h-[80vh] flex flex-col flex-1">
      {Success ? (
        <KYCStatus status="success" />
      ) : (
        <div className="flex flex-col md:items-center w-full px-3 justify-center pt-1">
          <div className="space-y-4 md:w-[384px]">
            <Label
              className="block text-3xl font-bold text-text-primary mb-3 pt-10 pb-5"
              htmlFor="signature_photo"
              text="Upload Signature"
            />
            {error && (
              <ErrorMessage message={error} onClose={() => setError("")} />
            )}

            {/* Option Buttons */}
            <div className="flex items-center justify-between space-x-3">
              <OptionButton
                label="Upload Signature"
                value="upload"
                option={option}
                setOption={handleChangeOption}
              />
              <OptionButton
                label="Draw Signature"
                value="draw"
                option={option}
                setOption={handleChangeOption}
              />
            </div>

            {/* Draw Signature */}
            {option === "draw" && (
              <SignaturePad
                setError={setError}
                setFormData={setFormData}
                handleClearSignature={handleClearSignature}
              />
            )}

            {/* Upload Signature */}
            {option === "upload" && (
              <>
                <p className="text-text-secondary text-xs tracking-tight py-3">
                  Upload or choose an image of your signature.
                </p>

                <InputFile
                  name="signature_photo"
                  id="signature_photo"
                  placeholder="Upload or choose an image of your signature"
                  onClear={handleClearSignature}
                  onChange={handleFileChange}
                  text="Upload Signature Image"
                  supportingDocument={formData.signature_photo}
                />

                <div className="py-3">
                  <hr className="border border-divider-border" />
                </div>
              </>
            )}

            {/* Navigation Buttons */}
            <div className="flex w-full md:w-96 space-x-4">
              <PrimaryButton onClick={handleContinue} text="Continue" />
            </div>
          </div>
        </div>
      )}
      {isLoading && <LoadingSpinner />}
    </div>
  );
};

export default ClientSignatureUpload;
